import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CalendarDays,
  User2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import "../../../App.css";
import SimpleBanner from "../../common/SimpleBanner";
import Noise from "../../animations/Noise";
import { blogs } from "./blogData";

const BlogDetails = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [currentImage, setCurrentImage] = useState(0);

  const blog = useMemo(
    () => blogs.find((item) => item.slug === slug),
    [slug]
  );

  /* ============================================================
     BLOG NOT FOUND
  ============================================================ */

  if (!blog) {
    return (
      <>
        <SimpleBanner
          title="Blog Details"
          subheading="Home / Blogs"
        />

        <section className="relative flex min-h-[70vh] items-center justify-center overflow-hidden theme-bg-primary px-6 font-saira">
          {/* Background Glow */}
          <div className="pointer-events-none absolute left-1/2 top-1/2 h-[320px] w-[320px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--brand-primary)]/10 blur-[120px]" />

          <div className="relative z-10 text-center">
            <h2 className="text-3xl font-bold theme-text-primary sm:text-4xl md:text-5xl">
              Blog Not Found
            </h2>

            <p className="mt-4 text-sm theme-text-muted sm:text-base">
              The article you are looking for does not exist.
            </p>

            <button
              onClick={() => navigate("/blogs")}
              className="
                mt-8
                rounded-full
                brand-bg
                px-7
                py-3
                font-semibold
                text-white
                transition-all
                duration-300
                hover:scale-105
              "
            >
              Back To Blogs
            </button>
          </div>
        </section>
      </>
    );
  }

  const images = blog.images?.length ? blog.images : [blog.image];

  const recentBlogs = blogs
    .filter((item) => item.slug !== slug)
    .slice(0, 6);

  return (
    <>
      <SimpleBanner
        title="Blog Details"
        subheading={`Home / Blogs / ${blog.title}`}
      />

      <section className="relative overflow-hidden theme-bg-primary py-8 font-saira md:py-10 xl:py-12">
        {/* ============================================================
            BACKGROUND EFFECTS
        ============================================================ */}

        {/* Brand Glow */}
        <div className="pointer-events-none absolute left-1/2 top-24 h-[380px] w-[380px] -translate-x-1/2 rounded-full bg-[var(--brand-primary)]/10 blur-[150px]" />

        {/* Noise */}
        <div className="pointer-events-none absolute inset-0 opacity-40">
          <Noise
            patternSize={250}
            patternScaleX={2}
            patternScaleY={2}
            patternRefreshInterval={2}
            patternAlpha={15}
          />
        </div>

        {/* Theme-Aware Grid */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `
              linear-gradient(
                to right,
                var(--text-primary) 1px,
                transparent 1px
              ),
              linear-gradient(
                to bottom,
                var(--text-primary) 1px,
                transparent 1px
              )
            `,
            backgroundSize: "70px 70px",
          }}
        />

        {/* ============================================================
            MAIN CONTAINER
        ============================================================ */}

        <div className="relative z-10 mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8 xl:px-10">
          <div className="grid grid-cols-1 gap-8 xl:grid-cols-12 xl:gap-10">
            {/* ========================================================
                LEFT CONTENT
            ======================================================== */}

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="xl:col-span-8"
            >
              {/* ======================================================
                  META
              ====================================================== */}

              <div className="flex flex-wrap items-center gap-3 sm:gap-5">
                {/* Category */}
                <span
                  className="
                    rounded-full
                    border
                    border-[var(--brand-primary)]/30
                    bg-[var(--brand-primary)]/10
                    px-4
                    py-2
                    text-[11px]
                    font-semibold
                    uppercase
                    tracking-[0.25em]
                    brand-color
                    sm:text-xs
                  "
                >
                  {blog.category}
                </span>

                {/* Date */}
                <div className="flex items-center gap-2 text-xs theme-text-muted sm:text-sm">
                  <CalendarDays size={16} />
                  <span>{blog.date}</span>
                </div>

                {/* Author */}
                <div className="flex items-center gap-2 text-xs theme-text-muted sm:text-sm">
                  <User2 size={16} />
                  <span>{blog.author}</span>
                </div>
              </div>

              {/* ======================================================
                  TITLE
              ====================================================== */}

              <h1
                className="
                  mt-6
                  max-w-5xl
                  text-3xl
                  font-bold
                  leading-tight
                  theme-text-primary
                  sm:text-4xl
                  md:text-5xl
                  xl:text-6xl
                "
              >
                {blog.title}
              </h1>

              {/* ======================================================
                  FEATURED IMAGE
              ====================================================== */}

              <div
                className="
                  relative
                  mt-8
                  overflow-hidden
                  rounded-[24px]
                  border
                  theme-border
                  theme-card
                  shadow-[0_20px_70px_var(--shadow-color)]
                  sm:mt-10
                "
              >
                <motion.img
                  key={currentImage}
                  initial={{ opacity: 0, scale: 1.08 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  src={images[currentImage]}
                  alt={blog.title}
                  className="
                    h-[240px]
                    w-full
                    object-cover
                    sm:h-[320px]
                    md:h-[430px]
                    lg:h-[520px]
                    xl:h-[620px]
                  "
                />

                {/* Theme-Aware Image Overlay */}
                <div
                  className="
                    pointer-events-none
                    absolute
                    inset-0
                    bg-gradient-to-t
                    from-black/70
                    via-black/10
                    to-transparent
                    dark:from-black/80
                  "
                />

                {/* ====================================================
                    IMAGE NAVIGATION
                ==================================================== */}

                {images.length > 1 && (
                  <>
                    {/* Previous */}
                    <button
                      type="button"
                      aria-label="Previous image"
                      onClick={() =>
                        setCurrentImage(
                          currentImage === 0
                            ? images.length - 1
                            : currentImage - 1
                        )
                      }
                      className="
                        absolute
                        left-3
                        top-1/2
                        flex
                        h-10
                        w-10
                        -translate-y-1/2
                        items-center
                        justify-center
                        rounded-full
                        border
                        border-white/20
                        bg-black/40
                        text-white
                        backdrop-blur-md
                        transition-all
                        duration-300
                        hover:bg-[var(--brand-primary)]
                        hover:text-white
                        sm:left-5
                        sm:h-12
                        sm:w-12
                      "
                    >
                      <ChevronLeft size={20} />
                    </button>

                    {/* Next */}
                    <button
                      type="button"
                      aria-label="Next image"
                      onClick={() =>
                        setCurrentImage(
                          currentImage === images.length - 1
                            ? 0
                            : currentImage + 1
                        )
                      }
                      className="
                        absolute
                        right-3
                        top-1/2
                        flex
                        h-10
                        w-10
                        -translate-y-1/2
                        items-center
                        justify-center
                        rounded-full
                        border
                        border-white/20
                        bg-black/40
                        text-white
                        backdrop-blur-md
                        transition-all
                        duration-300
                        hover:bg-[var(--brand-primary)]
                        hover:text-white
                        sm:right-5
                        sm:h-12
                        sm:w-12
                      "
                    >
                      <ChevronRight size={20} />
                    </button>

                    {/* Image Indicators */}
                    <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 gap-2">
                      {images.map((_, index) => (
                        <button
                          key={index}
                          type="button"
                          aria-label={`Go to image ${index + 1}`}
                          onClick={() => setCurrentImage(index)}
                          className={`
                            h-2.5
                            rounded-full
                            transition-all
                            duration-300
                            ${
                              currentImage === index
                                ? "w-8 bg-[var(--brand-primary)]"
                                : "w-2.5 bg-white/40"
                            }
                          `}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* ======================================================
                  ARTICLE
              ====================================================== */}

              <div
                className="
                  relative
                  mt-10
                  overflow-hidden
                  rounded-[24px]
                  border
                  theme-border
                  theme-card
                "
              >
                {/* Accent */}
                <div className="h-1 w-full bg-[var(--brand-primary)]" />

                {/* Glow */}
                <div className="pointer-events-none absolute -right-24 top-0 h-64 w-64 rounded-full bg-[var(--brand-primary)]/10 blur-[120px]" />

                <div className="relative z-10 p-5 sm:p-7 md:p-10 xl:p-12">
                  {/* Article Header */}
                  <div className="mb-8">
                    <span
                      className="
                        text-xs
                        font-bold
                        uppercase
                        tracking-[0.35em]
                        brand-color
                        sm:text-sm
                      "
                    >
                      ARTICLE
                    </span>

                    <h2 className="mt-3 text-2xl font-semibold theme-text-primary sm:text-3xl lg:text-4xl">
                      In-depth Overview
                    </h2>
                  </div>

                  {/* Article Content */}
                  <div className="space-y-6 text-[15px] leading-8 theme-text-secondary sm:text-base lg:text-[17px] lg:leading-9">
                    {blog.description
                      .split("\n")
                      .filter((item) => item.trim() !== "")
                      .map((paragraph, index) => (
                        <p key={index}>{paragraph}</p>
                      ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* ========================================================
                RIGHT SIDEBAR
            ======================================================== */}

            <motion.aside
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="xl:col-span-4"
            >
              {/* Sticky only on desktop */}
              <div className="xl:sticky xl:top-24 xl:h-[calc(240vh-8rem)]">
                {/* Sidebar */}
                <div
                  className="
                    relative
                    flex
                    h-full
                    flex-col
                    overflow-hidden
                    rounded-[24px]
                    border
                    theme-border
                    theme-card
                  "
                >
                  {/* Glow */}
                  <div className="pointer-events-none absolute -right-20 -top-20 h-60 w-60 rounded-full bg-[var(--brand-primary)]/10 blur-[120px]" />

                  {/* ==================================================
                      SIDEBAR HEADER
                  ================================================== */}

                  <div className="relative shrink-0 border-b theme-border px-6 py-6">
                    <span
                      className="
                        text-[11px]
                        font-bold
                        uppercase
                        tracking-[0.35em]
                        brand-color
                      "
                    >
                      MORE ARTICLES
                    </span>

                    <h3 className="mt-2 text-2xl font-semibold theme-text-primary lg:text-3xl">
                      Recent Blogs
                    </h3>
                  </div>

                  {/* ==================================================
                      SCROLLABLE BLOG LIST
                  ================================================== */}

                  <div
                    className="
                      custom-scrollbar
                      relative
                      flex-1
                      space-y-5
                      overflow-y-auto
                      p-5
                      xl:pr-3
                    "
                    style={{
                      scrollbarWidth: "thin",
                    }}
                  >
                    {recentBlogs.map((item) => (
                      <motion.div
                        key={item.id}
                        whileHover={{ y: -4 }}
                        transition={{ duration: 0.25 }}
                        onClick={() => {
                          window.scrollTo({
                            top: 0,
                            behavior: "smooth",
                          });

                          navigate(`/blogs/${item.slug}`);
                        }}
                        className="
                          group
                          cursor-pointer
                          overflow-hidden
                          rounded-2xl
                          border
                          theme-border
                          theme-card-secondary
                        "
                      >
                        {/* Image */}
                        <div className="overflow-hidden">
                          <img
                            src={item.image}
                            alt={item.title}
                            className="
                              h-44
                              w-full
                              object-cover
                              duration-500
                              group-hover:scale-110
                              sm:h-52
                              xl:h-44
                              2xl:h-52
                            "
                          />
                        </div>

                        {/* Content */}
                        <div className="p-5">
                          {/* Category */}
                          <span
                            className="
                              inline-flex
                              rounded-full
                              bg-[var(--brand-primary)]/10
                              px-3
                              py-1
                              text-[10px]
                              font-bold
                              uppercase
                              tracking-[0.28em]
                              brand-color
                            "
                          >
                            {item.category}
                          </span>

                          {/* Title */}
                          <h4
                            className="
                              mt-3
                              line-clamp-2
                              text-lg
                              font-semibold
                              leading-7
                              theme-text-primary
                              transition-colors
                              duration-300
                              group-hover:brand-color
                              lg:text-xl
                            "
                          >
                            {item.title}
                          </h4>

                          {/* Date */}
                          <div className="mt-3 flex items-center gap-2 text-xs theme-text-muted sm:text-sm">
                            <CalendarDays size={14} />
                            <span>{item.date}</span>
                          </div>

                          {/* Read Article */}
                          <button
                            type="button"
                            className="
                              mt-5
                              inline-flex
                              items-center
                              gap-2
                              font-semibold
                              brand-color
                              transition-transform
                              duration-300
                              group-hover:translate-x-2
                            "
                          >
                            Read Article
                            <ChevronRight size={18} />
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.aside>
          </div>

          {/* ============================================================
              BOTTOM CTA
          ============================================================ */}

          <div
            className="
              relative
              mt-10
              overflow-hidden
              rounded-[24px]
              border
              border-[var(--brand-primary)]/20
              theme-card-secondary
              sm:mt-14
              xl:mt-16
              xl:rounded-[32px]
            "
          >
            {/* Left Glow */}
            <div className="pointer-events-none absolute -left-20 top-1/2 h-64 w-64 -translate-y-1/2 rounded-full bg-[var(--brand-primary)]/20 blur-[130px]" />

            {/* Right Glow */}
            <div className="pointer-events-none absolute -right-20 top-1/2 h-64 w-64 -translate-y-1/2 rounded-full bg-[var(--brand-primary)]/20 blur-[130px]" />

            <div
              className="
                relative
                z-10
                flex
                flex-col
                gap-10
                px-6
                py-10
                sm:px-8
                sm:py-12
                lg:flex-row
                lg:items-center
                lg:justify-between
                lg:px-12
                xl:px-16
                xl:py-16
              "
            >
              {/* ======================================================
                  LEFT CTA CONTENT
              ====================================================== */}

              <div className="max-w-3xl">
                <span
                  className="
                    text-xs
                    font-bold
                    uppercase
                    tracking-[0.35em]
                    brand-color
                    sm:text-sm
                  "
                >
                  [ KEEP EXPLORING ]
                </span>

                <h2
                  className="
                    mt-4
                    text-3xl
                    font-semibold
                    leading-tight
                    theme-text-primary
                    sm:text-4xl
                    xl:text-5xl
                  "
                >
                  Continue Exploring Our
                  <span className="brand-color"> Latest Insights</span>
                </h2>

                <p
                  className="
                    mt-5
                    text-sm
                    leading-7
                    theme-text-muted
                    sm:text-base
                    sm:leading-8
                    xl:text-lg
                  "
                >
                  Discover more articles covering software engineering,
                  Artificial Intelligence, UI/UX design, cloud computing,
                  cybersecurity, automation, business growth and modern
                  technology trends. Stay updated with practical knowledge,
                  expert insights and innovative solutions from the Web_Bocket
                  team.
                </p>
              </div>

              {/* ======================================================
                  CTA BUTTON
              ====================================================== */}

              <div className="mt-8 flex justify-center">
                <motion.button
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  onClick={() => {
                    window.scrollTo({
                      top: 0,
                      behavior: "smooth",
                    });

                    navigate("/blogs");
                  }}
                  className="
                    group
                    flex
                    w-full
                    max-w-[340px]
                    items-center
                    justify-center
                    gap-3
                    rounded-full
                    brand-bg
                    px-6
                    py-3
                    text-sm
                    font-semibold
                    text-white
                    transition-all
                    duration-300
                    hover:brightness-110
                    sm:w-auto
                    sm:px-7
                    sm:py-3.5
                    sm:text-base
                    lg:px-8
                  "
                >
                  <span className="whitespace-nowrap">
                    View All Blogs
                  </span>

                  <span
                    className="
                      flex
                      h-8
                      w-8
                      flex-shrink-0
                      items-center
                      justify-center
                      rounded-full
                      bg-[var(--bg-primary)]
                      theme-text-primary
                      transition-all
                      duration-300
                      group-hover:rotate-45
                      group-hover:bg-white
                      group-hover:text-black
                      sm:h-9
                      sm:w-9
                    "
                  >
                    <ChevronRight size={16} />
                  </span>
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogDetails;